import dayjs from "dayjs";
import * as v from "valibot";

export type ShopStaff = {
  id: string;
  lastName: string;
  firstName: string;
  deactivatedAt: string | null;
};

// FieldsSchema は、 Customer や CustomerVisits のフォームのスキーマを表現します。
// フォームのそれぞれのフィールドが、どのようなデータ型を入力値として取るか、入力のバリデーションルール、入力のウィジェットなどを定義します。
// バックエンドから得られる、 CustomerFieldsSchema.schema, CustomerVisitFieldsSchema.schema は FieldsSchema の型を満たします。

// フィールドが有効かどうか
// スキーマを変更する際に、単純にフィールド定義を消してしまうと、過去のフォームのデータの定義がわからなくなってしまうので、
// inactive へ変更することで、新しく入力はできないが、過去のデータは表示を行えるようにしておく
const FieldState = v.union([v.literal("active"), v.literal("inactive")]);

const FieldScalarTypeName = v.union([
  v.literal("string"),
  v.literal("duration"),
  v.literal("id:ShopStaff"),
  v.literal("id:FileAttachment"),
]);
const FieldCollectionTypeName = v.union([v.literal("array")]);

const FieldDataType = v.union([
  v.object({
    type: FieldScalarTypeName,
  }),
  v.object({
    type: FieldCollectionTypeName,
    itemType: FieldScalarTypeName,
  }),
]);

const DisableAction = v.object({
  type: v.literal("disable"),
});

const EnableAction = v.object({
  type: v.literal("enable"),
});

const Option = v.object({
  label: v.string(),
  value: v.string(),
  disabled: v.optional(v.boolean()),
  nextValue: v.optional(v.union([v.string(), v.null_()])),
  groupId: v.optional(v.string()),
});

const SetOptionsAction = v.object({
  type: v.literal("setOptions"),
  options: v.array(Option),
});

// 日付オフセット計算: 参照フィールドの日付に対して月/日を加減算
const DateOffsetComputation = v.object({
  type: v.literal("dateOffset"),
  sourceField: v.string(), // 参照するフィールドのキー
  offset: v.object({
    months: v.optional(v.number()),
    days: v.optional(v.number()),
  }),
});

const Computation = v.union([DateOffsetComputation]);

// デフォルト値設定アクション: 条件を満たしたときにフィールドのデフォルト値を計算・設定
const SetDefaultValueAction = v.object({
  type: v.literal("setDefaultValue"),
  computation: Computation,
});

const FieldAction = v.union([
  DisableAction,
  EnableAction,
  SetOptionsAction,
  SetDefaultValueAction,
]);

const ConditionOperator = v.union([
  v.literal("equals"),
  v.literal("notEquals"),
  v.literal("in"),
  v.literal("notIn"),
]);

// フィールド条件: 特定のフィールドの値に関する条件
const FieldCondition = v.object({
  // 依存するフィールドのキー
  field: v.string(),
  // 比較演算子
  operator: ConditionOperator,
  // 比較対象の値 (operator によっては不要な場合もある)
  value: v.optional(v.union([v.unknown(), v.array(v.unknown())])),
});

// 論理条件: 複数の条件を組み合わせる (AND, OR, NOT) か、単一のフィールド条件
const LogicCondition = v.union([
  FieldCondition, // 単一フィールドに対する条件
  // v.object({ and: v.array(LogicCondition) }), // AND 条件
  // v.object({ or: v.array(LogicCondition) }),  // OR 条件
  // v.object({ not: LogicCondition }),          // NOT 条件
]);

// 条件ルール: どのような条件で、どのようなアクションを実行するか
const ConditionRule = v.object({
  // 条件: この条件が満たされた場合に action が実行される
  when: LogicCondition,
  // アクション: 条件が満たされた場合にフィールドに対して行う操作
  action: FieldAction,
});

const Widget = v.union([
  v.literal("text"),
  v.literal("textarea"),
  v.literal("select"),
  v.literal("combobox"),
  v.literal("multi-select"),
  v.literal("radio"),
  v.literal("files"),
  v.literal("couponUsage"),
]);

const RequiredValidator = v.object({
  type: v.literal("required"),
});

const Validator = v.union([RequiredValidator]);

const Options = v.array(Option);

const OptionGroup = v.object({
  id: v.string(),
  label: v.string(),
});

const InputType = v.union([
  v.literal("text"),
  v.literal("number"),
  v.literal("date"),
  v.literal("datetime-local"),
]);

const DisplayOptions = v.object({
  showAge: v.optional(v.boolean()),
});

const FieldDefinition = v.object({
  label: v.string(),
  dataType: FieldDataType,
  inputType: v.optional(InputType),
  options: v.optional(Options),
  optionGroups: v.optional(v.array(OptionGroup)),
  disabled: v.optional(v.boolean()),
  placeholder: v.optional(v.string()),
  validators: v.array(Validator),
  widget: Widget,
  displayOptions: v.optional(DisplayOptions),
});

const FieldDefinitionOverride = v.object({
  label: v.optional(v.string()),
  dataType: v.optional(FieldDataType),
  inputType: v.optional(InputType),
  options: v.optional(Options),
  disabled: v.optional(v.boolean()),
  placeholder: v.optional(v.string()),
  validators: v.optional(v.array(Validator)),
  widget: v.optional(Widget),
  displayOptions: v.optional(DisplayOptions),
});

// $ref で定義されたフィールドは、バックエンドから動的に定義を取得し、フォーム表示時に動的に解決されます。
// 現在は、 ShopStaff のセレクトボックスをサポート
const FieldDefinitionRef = v.pipe(v.string(), v.startsWith("$ref/"));

// サジェスト設定: 前回の CustomerVisit の値から今回の値をサジェストするための設定
const SuggestConfig = v.union([
  // 値をそのまま継承
  v.object({
    from: v.literal("previousVisit"),
    transform: v.literal("inherit"),
  }),
  // options の nextValue を参照
  v.object({
    from: v.literal("previousVisit"),
    transform: v.literal("nextValue"),
  }),
]);

const FieldSchema = v.object({
  key: v.string(),
  definition: v.union([FieldDefinition, FieldDefinitionRef]),
  definitionOverride: v.optional(FieldDefinitionOverride),
  state: FieldState,
  conditions: v.optional(v.array(ConditionRule)),
  suggest: v.optional(SuggestConfig),
});

const ResolvedFieldSchema = v.object({
  key: v.string(),
  definition: FieldDefinition,
  state: FieldState,
  conditions: v.optional(v.array(ConditionRule)),
  suggest: v.optional(SuggestConfig),
  // 条件ルールによって計算されたデフォルト値（setDefaultValue アクションの結果）
  computedDefaultValue: v.optional(v.string()),
  // computedDefaultValue の計算に使用されたソースフィールドのキー
  computedDefaultValueSourceField: v.optional(v.string()),
});

const FieldsSchemaMeta = v.object({
  version: v.literal("20250415"),
});

export const FieldsSchema = v.object({
  fields: v.array(FieldSchema),
  meta: FieldsSchemaMeta,
});

export type FieldsSchema = v.InferOutput<typeof FieldsSchema>;
export type FieldSchema = v.InferOutput<typeof FieldSchema>;
export type ResolvedFieldSchema = v.InferOutput<typeof ResolvedFieldSchema>;
export type FieldScalarTypeName = v.InferOutput<typeof FieldScalarTypeName>;
export type FieldDataType = v.InferOutput<typeof FieldDataType>;
export type ConditionRule = v.InferOutput<typeof ConditionRule>;
export type LogicCondition = v.InferOutput<typeof LogicCondition>;
export type ConditionOperator = v.InferOutput<typeof ConditionOperator>;
export type FieldAction = v.InferOutput<typeof FieldAction>;
export type Computation = v.InferOutput<typeof Computation>;
export type DisplayOptions = v.InferOutput<typeof DisplayOptions>;
export type FieldDefinition = v.InferOutput<typeof FieldDefinition>;
export type FieldDefinitionOverride = v.InferOutput<
  typeof FieldDefinitionOverride
>;
export type FieldDefinitionRef = v.InferOutput<typeof FieldDefinitionRef>;
export type SuggestConfig = v.InferOutput<typeof SuggestConfig>;
export type Option = v.InferOutput<typeof Option>;
export type OptionGroup = v.InferOutput<typeof OptionGroup>;
export type Validator = v.InferOutput<typeof Validator>;
export type Widget = v.InferOutput<typeof Widget>;

const isFieldDefinitionRef = (value: unknown): value is FieldDefinitionRef => {
  return v.safeParse(FieldDefinitionRef, value).success;
};

export const validateFieldsSchema = (value: unknown): FieldsSchema => {
  const result = v.safeParse(FieldsSchema, value);
  if (result.success === false) {
    throw new Error(`Invalid fields schema: ${result.issues}`);
  }
  return result.output;
};

export const findFieldFromSchema = (schema: FieldsSchema, key: string) => {
  const field = schema.fields.find((field) => field.key === key);
  if (!field) {
    throw new Error(`Field ${key} not found`);
  }
  return field;
};

const createShopStaffSelectDefinition = ({
  shopStaffs,
}: {
  shopStaffs: ShopStaff[];
}): FieldDefinition => {
  return {
    label: "担当者",
    dataType: {
      type: "id:ShopStaff",
    },
    options: shopStaffs.map((staff) => ({
      label: `${staff.lastName} ${staff.firstName}`,
      value: staff.id,
      disabled: staff.deactivatedAt != null,
    })),
    validators: [
      {
        type: "required",
      },
    ],
    widget: "select",
  };
};

export type FieldsContext = {
  shopStaffs?: ShopStaff[];
};

export const resolveFieldDefinition = ({
  field,
  context,
}: {
  field: FieldSchema;
  context: FieldsContext;
}): ResolvedFieldSchema => {
  const definition = field.definition;
  if (isFieldDefinitionRef(definition)) {
    if (definition === "$ref/shopStaff/select") {
      const definition = {
        ...createShopStaffSelectDefinition({
          shopStaffs: context.shopStaffs ?? [],
        }),
        ...field.definitionOverride,
      };

      return {
        ...field,
        definition,
      };
    }

    throw new Error(`Unknown ref: ${definition}`);
  }

  return {
    ...field,
    definition,
  };
};

// 条件を評価する
const evaluateCondition = (
  condition: LogicCondition,
  formData: Record<string, unknown>,
): boolean => {
  const { field, operator, value } = condition;
  const targetValue = formData[field];

  switch (operator) {
    case "equals":
      return targetValue === value;
    case "notEquals":
      return targetValue !== value;
    case "in":
      return Array.isArray(value) && value.includes(targetValue);
    case "notIn":
      return Array.isArray(value) && !value.includes(targetValue);
    default: {
      const _exhaustiveCheck: never = operator;
      return false;
    }
  }
};

// 日付オフセット計算
// dayjs を使用して月末処理を適切に行う（例: 1/31 + 1ヶ月 = 2/28）
const computeDateOffset = (
  sourceValue: string,
  offset: { months?: number; days?: number },
): string | undefined => {
  const date = dayjs(sourceValue);
  if (!date.isValid()) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[adaptiveForm] computeDateOffset: 無効な日付文字列が渡されました`,
        { sourceValue, offset },
      );
    }
    return undefined;
  }

  let result = date;
  if (offset.months) {
    result = result.add(offset.months, "month");
  }
  if (offset.days) {
    result = result.add(offset.days, "day");
  }

  // ソースの形式に合わせて出力形式を決定
  // yyyy-MM-dd (10文字) → 日付のみ
  // yyyy-MM-ddTHH:mm (16文字) → 日時
  if (sourceValue.length <= 10) {
    return result.format("YYYY-MM-DD");
  }

  return result.format("YYYY-MM-DDTHH:mm");
};

// 計算を実行する
const executeComputation = (
  computation: Computation,
  formData: Record<string, unknown>,
): string | undefined => {
  if (computation.type === "dateOffset") {
    // 参照フィールドが存在しない場合は undefined を返す
    // （サジェスト計算時など、一部のフィールドしか formData に含まれないケースがある）
    if (!(computation.sourceField in formData)) {
      return undefined;
    }

    const sourceValue = formData[computation.sourceField];
    if (typeof sourceValue !== "string" || sourceValue === "") {
      return undefined;
    }
    return computeDateOffset(sourceValue, computation.offset);
  }

  const _exhaustiveCheck: never = computation.type;
  return undefined;
};

export const applyConditionRules = ({
  field,
  formData,
}: {
  field: ResolvedFieldSchema;
  formData: Record<string, unknown>;
}): ResolvedFieldSchema => {
  let appliedField = { ...field };
  if (!field.conditions) {
    return appliedField;
  }

  for (const condition of field.conditions) {
    const meetsCondition = evaluateCondition(condition.when, formData);

    if (!meetsCondition) {
      continue;
    }

    if (condition.action.type === "disable") {
      appliedField = {
        ...appliedField,
        definition: {
          ...appliedField.definition,
          disabled: true,
        },
      };
    }

    if (condition.action.type === "setOptions") {
      appliedField = {
        ...appliedField,
        definition: {
          ...appliedField.definition,
          options: condition.action.options,
          disabled: false,
        },
      };
    }

    if (condition.action.type === "setDefaultValue") {
      const computation = condition.action.computation;
      const computedValue = executeComputation(computation, formData);
      if (computedValue !== undefined) {
        appliedField = {
          ...appliedField,
          computedDefaultValue: computedValue,
          // ソースフィールドを記録（dateOffset の場合）
          computedDefaultValueSourceField:
            computation.type === "dateOffset"
              ? computation.sourceField
              : undefined,
        };
      }
    }
  }

  return appliedField;
};

export const getFieldSchemasWithConditionRulesApplied = (
  schema: FieldsSchema,
  formData: Record<string, unknown>,
  context: FieldsContext,
): ResolvedFieldSchema[] => {
  const fieldSchemas = schema.fields;
  return fieldSchemas.map((fieldSchema) =>
    applyConditionRules({
      field: resolveFieldDefinition({ field: fieldSchema, context }),
      formData,
    }),
  );
};
